import { ref, computed } from 'vue'
import type { AuditResult } from '@/content-review/api'

export function parsePartialAuditResults(content: string): AuditResult[] {
  const match = content.match(/"audit_results"\s*:\s*\[/)
  if (!match) return []

  const arrayStartIndex = match.index! + match[0].length
  const contentFromArray = content.substring(arrayStartIndex)
  const results: AuditResult[] = []
  let depth = 0
  let currentObject = ''
  let inString = false
  let escapeNext = false

  for (let i = 0; i < contentFromArray.length; i++) {
    const char = contentFromArray[i]

    if (escapeNext) {
      currentObject += char
      escapeNext = false
      continue
    }
    if (char === '\\') {
      escapeNext = true
      currentObject += char
      continue
    }
    if (char === '"') {
      inString = !inString
      currentObject += char
      continue
    }

    if (!inString) {
      if (char === '{') {
        depth++
        currentObject += char
      } else if (char === '}') {
        currentObject += char
        depth--
        if (depth === 0 && currentObject.trim()) {
          try {
            results.push(JSON.parse(currentObject.trim()))
            currentObject = ''
          } catch {
            // swallow incomplete objects
          }
        }
      } else if (depth > 0) {
        currentObject += char
      }
    } else {
      currentObject += char
    }
  }

  return results
}

export type ContentReviewFn = (opts: {
  signal: AbortSignal
  onProgress: (partial: string) => void
}) => Promise<{ audit_results: AuditResult[] }>

const DEFAULT_REVIEW_ERROR = 'Unable to load impact statement review scores. Try again in a moment.'

export function useContentReview({ errorMessage = DEFAULT_REVIEW_ERROR }: { errorMessage?: string } = {}) {
  const isReviewing = ref(false)
  const reviewResults = ref<AuditResult[]>([])
  const reviewError = ref<string | null>(null)
  const abortController = ref<AbortController | null>(null)

  const hasResults = computed(() => reviewResults.value.length > 0)

  async function startReview(reviewFn: ContentReviewFn, onComplete?: (results: AuditResult[]) => void) {
    if (abortController.value) abortController.value.abort()
    abortController.value = new AbortController()
    const currentController = abortController.value
    isReviewing.value = true
    reviewResults.value = []
    reviewError.value = null

    try {
      const result = await reviewFn({
        signal: currentController.signal,
        onProgress: (partialContent) => {
          const parsed = parsePartialAuditResults(partialContent)
          if (parsed.length > 0) reviewResults.value = parsed
        },
      })
      if (currentController.signal.aborted) return
      if (result.audit_results?.length > 0) {
        reviewResults.value = result.audit_results
      }
      onComplete?.(reviewResults.value)
    } catch (err) {
      if (currentController.signal.aborted) return
      if ((err as Error).name !== 'AbortError') {
        reviewError.value = errorMessage
      }
    } finally {
      if (abortController.value === currentController) {
        isReviewing.value = false
      }
    }
  }

  function cancelReview() {
    abortController.value?.abort()
  }

  function clearResults() {
    reviewResults.value = []
    reviewError.value = null
  }

  return { isReviewing, reviewResults, reviewError, hasResults, startReview, cancelReview, clearResults }
}
